import { useEffect, useMemo, useState } from "react";
import {
  addMonths,
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { tr } from "date-fns/locale";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Filter,
  List,
  RotateCcw,
  Rows3,
  Search,
  Users,
} from "lucide-react";
import { api, ApiError, query } from "../api";
import type {
  CalendarDay,
  CalendarMission,
  Handover,
  LeaveRequest,
  LeaveType,
  RequestStatus,
  ShiftChange,
  Team,
  User,
  ShiftWeek,
  Shift,
  OrganizationUnit,
} from "../types";
import {
  SCHEDULE_PATTERNS,
  type SchedulePattern,
} from "../../shared/domain-types";
import { getConsecutiveOffDays } from "../../shared/handoff";
import { getShiftForPattern } from "../../shared/shifts";
import {
  dateLabel,
  dateRangeLabel,
  LEAVE_LABELS,
  SHIFT_LABELS,
  STATUS_LABELS,
  skillLabel,
  todayIso,
} from "../utils";
import {
  Alert,
  Button,
  Empty,
  Loading,
  Modal,
  PageHeader,
  SelectField,
  ShiftBadge,
  StatusBadge,
} from "../components/ui";

type View = "month" | "week" | "list";
type CalendarEvent = LeaveRequest | ShiftChange | CalendarMission;

interface CalendarListEntry {
  key: string;
  event: CalendarEvent;
  startDate: string;
  endDate: string;
}
interface CalendarResponse {
  days: CalendarDay[];
  handovers?: Handover[];
  personnel?: User[];
  shiftWeek?: ShiftWeek | null;
  start?: string;
  end?: string;
}

const teams: Team[] = ["A", "B", "C", "D"];
const calendarUnits: Array<{ code: Exclude<OrganizationUnit, "R1_R2">; label: string }> = [
  { code: "COMPOSITE", label: "Kompozit" },
  { code: "STRUCTURAL", label: "Yapısal" },
  { code: "PAINT", label: "Boya" },
];
const leaveTypes: LeaveType[] = [
  "ANNUAL",
  "EXCUSE",
  "MARRIAGE",
  "BEREAVEMENT",
  "MEDICAL",
  "TRAINING",
  "ASSIGNMENT",
  "FLIGHT",
  "MILITARY",
  "OTHER",
];
const statuses: RequestStatus[] = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "CANCELLATION_PENDING",
  "CHANGE_PENDING",
];

function teamFromHash(): Team | null {
  const search = window.location.hash.split("?", 2)[1] ?? "";
  const value = new URLSearchParams(search).get("team");
  return value && teams.includes(value as Team) ? (value as Team) : null;
}

function dayEvents(
  day: CalendarDay,
  person: string,
  type: string,
  status: string,
  allowedUserIds?: ReadonlySet<string>,
): Array<LeaveRequest | ShiftChange | CalendarMission> {
  const visible = (userId: string | number) => !allowedUserIds || allowedUserIds.has(String(userId));
  const leaves = (day.leaves ?? []).filter(
    (item) =>
      visible(item.userId) &&
      (!person || String(item.userId) === person) &&
      (!type || item.type === type) &&
      (!status || item.status === status),
  );
  const shifts = (day.shiftChanges ?? []).filter(
    (item) =>
      visible(item.userId) &&
      (!person || String(item.userId) === person) &&
      !type &&
      (!status || item.status === status),
  );
  const missions = (day.missions ?? []).filter(
    (item) => visible(item.userId) && (!person || String(item.userId) === person) && !type && !status,
  );
  return [...leaves, ...shifts, ...missions];
}

function shortLeaveName(displayName: string) {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length < 2) return displayName;
  return `${parts[0]} ${parts.at(-1)?.charAt(0) ?? ""}.`;
}

function sharedUnitPrefix(leaveUnit: OrganizationUnit | undefined, selectedUnit: OrganizationUnit) {
  if (!leaveUnit || leaveUnit === selectedUnit) return "";
  return leaveUnit === "PAINT" ? "Boya: " : leaveUnit === "R1_R2" ? "R1/R2: " : "";
}

function leaveDurationMark(duration: LeaveRequest["duration"]) {
  if (duration === "HALF") return " ½";
  if (duration === "TWO_HOURS") return " 2s";
  return "";
}

function userSchedulePattern(user: User): SchedulePattern | null {
  const value = user.schedulePattern ?? user.assignment?.schedulePattern;
  return SCHEDULE_PATTERNS.includes(value as SchedulePattern)
    ? (value as SchedulePattern)
    : null;
}

function personalShiftLabel(
  pattern: SchedulePattern,
  date: string,
  shift: Shift,
) {
  if (shift !== "OFF") return SHIFT_LABELS[shift];
  return getConsecutiveOffDays(pattern, date) >= 3
    ? `${pattern} • 3 OFF`
    : `${pattern} OFF`;
}

function offPatternsForTeam(team: Team, date: string): SchedulePattern[] {
  return SCHEDULE_PATTERNS.filter(
    (pattern) => pattern.startsWith(team) && getShiftForPattern(pattern, date) === "OFF",
  );
}

function ManagementDayDetails({ date, skill }: { date: string; skill: string }) {
  const [items, setItems] = useState<Array<{ unit: OrganizationUnit; team: Team; hours: string; day: CalendarDay; handovers: Handover[] }> | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setItems(null); setError("");
    api<{ items: NonNullable<typeof items> }>(`/api/calendar/management-day${query({ date })}`)
      .then((value) => { if (active) setItems(value.items); })
      .catch((e) => { if (active) setError(e instanceof ApiError ? e.message : "Ekip ayrıntıları yüklenemedi."); });
    return () => { active = false; };
  }, [date]);
  if (error) return <Alert tone="danger">{error}</Alert>;
  if (!items) return <Loading label="Aynı vardiyadaki ekipler yükleniyor" />;
  return <div className="management-day-units">{items.length ? items.map((item) => <section key={`${item.unit}-${item.team}`}>
    <h2>{item.unit === "COMPOSITE" ? "Kompozit" : "Yapısal"} · {item.team} Ekibi</h2>
    <p>Ortak çalışma saatleri: {item.hours}</p>
    <DayDetails day={item.day} handovers={item.handovers} skill={skill} />
  </section>) : <p>Bu gün için eşleşen ekip yok.</p>}</div>;
}

function CalendarShiftBadge({ shift, label }: { shift: Shift; label?: string }) {
  return (
    <span className={`shift shift--${shift.toLowerCase()}`}>
      {label ?? SHIFT_LABELS[shift]}
    </span>
  );
}

function DayDetails({
  day,
  handovers,
  displayShift,
  displayShiftLabel,
  skill,
}: {
  day: CalendarDay;
  handovers?: Handover[];
  displayShift?: Shift;
  displayShiftLabel?: string;
  skill?: string;
}) {
  const people = (day.people ?? []).filter((person) => !skill || person.skills?.includes(skill));
  const stateOf = (person: CalendarDay["people"][number]) => person.attendanceState
    ?? (person.mission
      ? "MISSION"
      : person.leave?.duration === "FULL" && ["APPROVED", "CANCELLATION_PENDING", "CHANGE_PENDING"].includes(person.leave.status)
        ? "LEAVE"
        : person.shift === "OFF" ? "OFF" : "WORKING");
  const working = people.filter((person) => stateOf(person) === "WORKING");
  const away = people.filter((person) => stateOf(person) !== "WORKING");
  const noteFor = (person: CalendarDay["people"][number]) => {
    if (person.mission) return `Görevde: ${person.mission.location}`;
    if (stateOf(person) === "OFF") return person.offSequence && person.offSequence >= 3 ? "3 OFF" : "OFF";
    if (person.leave) return `${LEAVE_LABELS[person.leave.type]} · ${STATUS_LABELS[person.leave.status]}`;
    if (stateOf(person) === "SHIFT_CHANGED" && person.shiftChange) return `Vardiya değişikliği · ${SHIFT_LABELS[person.shiftChange.requestedShift]} vardiyasına geçti`;
    if (person.shiftChange) return `Vardiya değişikliği ile ${SHIFT_LABELS[person.shiftChange.requestedShift]} vardiyasına gelecek`;
    return "Planlanan vardiyada";
  };
  const group = (title: string, entries: typeof people, tone: "working" | "away") => (
    <section className={`day-attendance-group day-attendance-group--${tone}`}>
      <header><h3>{title}</h3><span>{entries.length} kişi</span></header>
      {entries.length ? <div className="people-shifts">
        {entries.map((person) => {
          const leaveDetail = person.leave ? day.leaves?.find((leave) => String(leave.id) === String(person.leave?.id)) : null;
          return <article key={person.userId}>
            <div className="person-shift-identity">
              <strong>{person.displayName}</strong>
              <span>{person.schedulePattern}</span>
              {person.skills?.length ? <span className="person-skill-list">{person.skills.map((item) => <i key={item}>{skillLabel(item)}</i>)}</span> : null}
            </div>
            <ShiftBadge shift={person.shift} />
            <span className={`event-note ${person.mission ? "event-note--mission" : ""}`}>{noteFor(person)}</span>
            {leaveDetail?.description ? <small className="private-event-description">Açıklama: {leaveDetail.description}</small> : null}
          </article>;
        })}
      </div> : <p className="day-attendance-empty">Bu grupta personel yok.</p>}
    </section>
  );
  return (
    <div className="day-details">
      {handovers?.length ? (
        <section className="day-handover">
          <span className="eyebrow">TAHMİNİ İŞ DEVRİ</span>
          {handovers.map((handover) => <strong key={`${handover.fromShift ?? "GECE"}-${handover.toShift ?? "SABAH"}`}>
            {handover.fromName ?? `${handover.nightTeam} ekibinden başka personel`}
            <span aria-hidden="true"> → </span>
            {handover.toName ?? `${handover.morningTeam} ekibinden başka personel`}
          </strong>)}
          {[...new Set(handovers.flatMap((handover) => handover.warnings ?? []))].map((warning) => (
            <small key={warning}>{warning}</small>
          ))}
        </section>
      ) : null}
      <div className="day-details__summary">
        <CalendarShiftBadge
          shift={displayShift ?? day.teamShift}
          label={displayShiftLabel}
        />
        <span>
          {working.length} vardiyaya gelecek · {away.length} gelmeyecek/OFF
        </span>
      </div>
      {people.length ? <div className="day-attendance-grid">{group("Vardiyaya gelecekler", working, "working")}{group("Gelmeyecekler / OFF", away, "away")}</div> : (
        <Empty
          icon={<Users />}
          title="Personel bilgisi yok"
          text="Bu gün için ayrıntı bulunamadı."
        />
      )}
    </div>
  );
}

export function CalendarScreen({
  currentUser,
  navigate,
}: {
  currentUser: User;
  navigate: (path: string) => void;
}) {
  const [team, setTeam] = useState<Team>(
    teamFromHash() ?? currentUser.mainTeam ?? currentUser.assignment?.mainTeam ?? "A",
  );
  const [unit, setUnit] = useState<OrganizationUnit>(
    currentUser.organizationUnit ?? "COMPOSITE",
  );
  const [view, setView] = useState<View>("month");
  const [cursor, setCursor] = useState(parseISO(todayIso()));
  const [data, setData] = useState<CalendarResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<CalendarDay | null>(null);
  const [selectedOff, setSelectedOff] = useState<{
    date: string;
    patterns: SchedulePattern[];
  } | null>(null);
  const [person, setPerson] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [skill, setSkill] = useState("");
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [monthPickerYear, setMonthPickerYear] = useState(cursor.getFullYear());
  const ownTeam = currentUser.mainTeam ?? currentUser.assignment?.mainTeam;
  const ownPattern = userSchedulePattern(currentUser);
  const showPersonalPattern = unit === currentUser.organizationUnit && team === ownTeam && ownPattern !== null;

  function displayedShift(day: CalendarDay): Shift {
    if (!showPersonalPattern || !ownPattern) return day.teamShift;
    return day.people?.find((person) => String(person.userId) === String(currentUser.id))?.shift
      ?? getShiftForPattern(ownPattern, day.date);
  }

  function displayedShiftLabel(day: CalendarDay) {
    const shift = displayedShift(day);
    if (!showPersonalPattern || !ownPattern) return SHIFT_LABELS[shift];
    const ownPerson = day.people?.find((person) => String(person.userId) === String(currentUser.id));
    return ownPerson?.shiftChange
      ? `${SHIFT_LABELS[shift]} • Vardiya değişikliği`
      : personalShiftLabel(ownPattern, day.date, shift);
  }

  const dateWindow = useMemo(() => {
    if (view === "month")
      return {
        start: format(
          startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }),
          "yyyy-MM-dd",
        ),
        end: format(
          endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }),
          "yyyy-MM-dd",
        ),
      };
    if (view === "week")
      return {
        start: format(cursor, "yyyy-MM-dd"),
        end: format(cursor, "yyyy-MM-dd"),
      };
    return {
      start: format(startOfMonth(cursor), "yyyy-MM-dd"),
      end: format(endOfMonth(cursor), "yyyy-MM-dd"),
    };
  }, [cursor, view]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api<CalendarResponse>(
      `/api/calendar${query({ unit, team, start: dateWindow.start, end: dateWindow.end, view })}`,
    )
      .then((result) => {
        if (active) setData(Array.isArray(result) ? { days: result } : result);
      })
      .catch((caught) => {
        if (active)
          setError(
            caught instanceof ApiError ? caught.message : "Takvim yüklenemedi.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [unit, team, dateWindow.start, dateWindow.end, view]);

  const byDate = useMemo(
    () => new Map((data?.days ?? []).map((day) => [day.date, day])),
    [data],
  );
  const monthDates = useMemo(
    () =>
      eachDayOfInterval({
        start: parseISO(dateWindow.start),
        end: parseISO(dateWindow.end),
      }),
    [dateWindow.start, dateWindow.end],
  );
  const personnel = useMemo<User[]>(() => {
    if (data?.personnel?.length) return data.personnel;
    const seen = new Map<string, User>();
    for (const day of data?.days ?? [])
      for (const entry of day.people ?? [])
        seen.set(String(entry.userId), {
          id: entry.userId,
          loginCode: "",
          displayName: entry.displayName,
          role: "employee",
        });
    return [...seen.values()];
  }, [data]);
  const personnelById = useMemo(
    () => new Map(personnel.map((item) => [String(item.id), item])),
    [personnel],
  );
  const skillOptions = useMemo(() => [...new Set(personnel.flatMap((item) => item.skills ?? []))]
    .sort((left, right) => skillLabel(left).localeCompare(skillLabel(right), "tr-TR")), [personnel]);
  const visibleUserIds = useMemo(() => skill
    ? new Set(personnel.filter((item) => item.skills?.includes(skill)).map((item) => String(item.id)))
    : undefined, [personnel, skill]);
  const filteredDays = useMemo(
    () => (data?.days ?? []).filter((day) => {
      const rosterMatch = day.people.some((entry) =>
        (!person || String(entry.userId) === person)
        && (!visibleUserIds || visibleUserIds.has(String(entry.userId))));
      return dayEvents(day, person, type, status, visibleUserIds).length > 0
        || (!type && !status && (person || skill) && rosterMatch)
        || (!person && !type && !status && !skill);
    }),
    [data, person, skill, status, type, visibleUserIds],
  );
  const listEntries = useMemo<CalendarListEntry[]>(() => {
    const entries = new Map<string, CalendarListEntry>();
    for (const day of filteredDays) {
      for (const event of dayEvents(day, person, type, status, visibleUserIds)) {
        const kind = "type" in event ? "leave" : "location" in event ? "mission" : "shift";
        const key = `${kind}:${event.id}`;
        const existing = entries.get(key);
        if (existing) {
          if (day.date < existing.startDate) existing.startDate = day.date;
          if (day.date > existing.endDate) existing.endDate = day.date;
        } else {
          entries.set(key, {
            key,
            event,
            startDate: "type" in event ? event.startDate : day.date,
            endDate: "type" in event ? event.endDate : day.date,
          });
        }
      }
    }
    return [...entries.values()].sort((left, right) =>
      left.startDate.localeCompare(right.startDate) || left.key.localeCompare(right.key));
  }, [filteredDays, person, status, type, visibleUserIds]);

  function openListEntry(entry: CalendarListEntry) {
    if ("type" in entry.event) navigate(`requests?focus=${encodeURIComponent(String(entry.event.id))}`);
    else if ("location" in entry.event) navigate(`missions?focus=${encodeURIComponent(String(entry.event.id))}`);
    else navigate(`requests?focus=${encodeURIComponent(String(entry.event.id))}`);
  }

  function move(amount: number) {
    setCursor((value) =>
      view === "week"
        ? addDays(value, 8 * amount)
        : amount > 0
          ? addMonths(value, 1)
          : subMonths(value, 1),
    );
  }
  function today() {
    setCursor(parseISO(todayIso()));
  }
  return (
    <div className="page">
      <PageHeader
        eyebrow="ORTAK GÖRÜNÜM"
        title="Ekip takvimi"
        description="A, B, C ve D ekiplerinin izin, yokluk ve vardiya değişiklikleri."
        actions={
          ((unit === "R1_R2" && currentUser.organizationUnit === "R1_R2" && currentUser.role === "supervisor")
            || (ownTeam === team && currentUser.organizationUnit === unit)) ? (
            <Button onClick={() => navigate("requests?create=leave")}>
              <CalendarDays size={18} />
              Talep oluştur
            </Button>
          ) : null
        }
      />
      <div className="unit-tabs segmented" role="tablist" aria-label="Birim seçin">
        {currentUser.organizationUnit === "R1_R2" ? (
          <button role="tab" aria-selected={unit === "R1_R2"} className={unit === "R1_R2" ? "active" : ""} onClick={() => setUnit("R1_R2")}>
            R1/R2 Vardiyam
          </button>
        ) : null}
        {calendarUnits.map((item) => (
          <button key={item.code} role="tab" aria-selected={unit === item.code}
            className={unit === item.code ? "active" : ""} onClick={() => setUnit(item.code)}>
            {item.label}
          </button>
        ))}
      </div>
      {unit !== "R1_R2" ? <div className="team-tabs" role="tablist" aria-label="Ekip seçin">
        {teams.map((item) => (
          <button
            role="tab"
            aria-selected={team === item}
            className={team === item ? `active team-${item.toLowerCase()}` : ""}
            onClick={() => setTeam(item)}
            key={item}
          >
            <span>{item}</span>
            {item} Ekibi
          </button>
        ))}
      </div> : null}
      <div className="calendar-toolbar">
        <div className="segmented" aria-label="Takvim görünümü">
          <button
            className={view === "month" ? "active" : ""}
            onClick={() => setView("month")}
          >
            <CalendarDays />
            Aylık
          </button>
          <button
            className={view === "week" ? "active" : ""}
            onClick={() => setView("week")}
          >
            <Rows3 />
            Haftalık
          </button>
          <button
            className={view === "list" ? "active" : ""}
            onClick={() => setView("list")}
          >
            <List />
            Liste
          </button>
        </div>
        <div className="calendar-nav">
          <button aria-label="Önceki dönem" onClick={() => move(-1)}>
            <ChevronLeft />
          </button>
          <button
            type="button"
            className="calendar-period-button"
            aria-label="Ay ve yıl seç"
            onClick={() => { setMonthPickerYear(cursor.getFullYear()); setMonthPickerOpen(true); }}
          >
            {view === "week" && data?.shiftWeek
              ? `${dateLabel(data.shiftWeek.startDate, "short")} – ${dateLabel(data.shiftWeek.endDate, "short")}`
              : format(cursor, "LLLL yyyy", { locale: tr })}
          </button>
          <button aria-label="Sonraki dönem" onClick={() => move(1)}>
            <ChevronRight />
          </button>
          <Button variant="ghost" onClick={today}>
            Bugün
          </Button>
        </div>
      </div>
      <details className="filters">
        <summary>
          <Filter size={18} />
          Filtreler
          {person || type || status || skill ? (
            <span className="filter-count">
              {[person, type, status, skill].filter(Boolean).length}
            </span>
          ) : null}
        </summary>
        <div className="filters__body">
          <SelectField
            label="Personel"
            value={person}
            onChange={(event) => setPerson(event.target.value)}
          >
            <option value="">Tüm personel</option>
            {personnel.map((item) => (
              <option value={item.id} key={item.id}>
                {item.displayName}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Skill"
            value={skill}
            onChange={(event) => setSkill(event.target.value)}
          >
            <option value="">Tüm skill'ler</option>
            {skillOptions.map((item) => (
              <option value={item} key={item}>{skillLabel(item)}</option>
            ))}
          </SelectField>
          <SelectField
            label="İzin türü"
            value={type}
            onChange={(event) => setType(event.target.value)}
          >
            <option value="">Tüm türler</option>
            {leaveTypes.map((item) => (
              <option value={item} key={item}>
                {LEAVE_LABELS[item]}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Durum"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Tüm durumlar</option>
            {statuses.map((item) => (
              <option value={item} key={item}>
                {STATUS_LABELS[item]}
              </option>
            ))}
          </SelectField>
          <Button
            variant="ghost"
            onClick={() => {
              setPerson("");
              setType("");
              setStatus("");
              setSkill("");
            }}
          >
            <RotateCcw size={17} />
            Temizle
          </Button>
        </div>
      </details>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {loading && data ? <div className="calendar-refresh-status" role="status" aria-live="polite">Takvim güncelleniyor…</div> : null}
      {loading && !data ? (
        <Loading label="Takvim hazırlanıyor" />
      ) : view === "month" ? (
        <div className="month-calendar">
          <div className="weekday-row">
            {["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"].map((name) => (
              <span key={name}>{name}</span>
            ))}
          </div>
          <div className="month-grid">
            {monthDates.map((date) => {
              const iso = format(date, "yyyy-MM-dd");
              const day = byDate.get(iso);
              const offPatterns = day && day.teamShift !== "OFF"
                ? offPatternsForTeam(team, iso)
                : [];
              const approvedLeaves = (day?.leaves ?? []).filter(
                (leave) =>
                  (!visibleUserIds || visibleUserIds.has(String(leave.userId))) &&
                  leave.status === "APPROVED" &&
                  (!person || String(leave.userId) === person) &&
                  (!type || leave.type === type) &&
                  (!status || status === "APPROVED"),
              );
              const calendarMissions = (day?.missions ?? []).filter(
                (mission) => (!visibleUserIds || visibleUserIds.has(String(mission.userId)))
                  && (!person || String(mission.userId) === person) && !type && !status,
              );
              const calendarShiftChanges = (day?.shiftChanges ?? []).filter(
                (change) => (!visibleUserIds || visibleUserIds.has(String(change.userId)))
                  && (!person || String(change.userId) === person)
                  && !type
                  && (!status || change.status === status),
              );
              const dimmed = !isSameMonth(date, cursor);
              return (
                <div
                  className={`calendar-cell ${dimmed ? "calendar-cell--dim" : ""} ${iso === todayIso() ? "calendar-cell--today" : ""}`}
                  key={iso}
                >
                  <button
                    type="button"
                    className="calendar-cell__open"
                    disabled={!day}
                    onClick={() => day && setSelected(day)}
                    aria-label={`${dateLabel(iso)}, ${approvedLeaves.length} izinli, ${offPatterns.length} 3 OFF, ${calendarShiftChanges.length} vardiya değişikliği, ${calendarMissions.length} görevli; gün ayrıntısını aç`}
                  >
                    <span className="calendar-cell__day">{format(date, "d")}</span>
                    {day ? <>
                      <span
                        className={`mini-shift mini-shift--${day.teamShift.toLowerCase()}`}
                      >
                        {SHIFT_LABELS[day.teamShift]}
                      </span>
                      <span className="calendar-cell__desktop-events">
                        {approvedLeaves.slice(0, 3).map((leave) => (
                          <span className="calendar-leave-name" key={`leave-${leave.id}`}>
                            {sharedUnitPrefix(leave.unit, unit)}{shortLeaveName(leave.displayName)}{leaveDurationMark(leave.duration)}
                          </span>
                        ))}
                        {approvedLeaves.length > 3 ? <span className="more-events">+{approvedLeaves.length - 3} kişi</span> : null}
                        {calendarShiftChanges.slice(0, 2).map((change) => (
                          <span className="calendar-shift-change-name" key={`shift-${change.id}`}>
                            {shortLeaveName(change.displayName)} → {SHIFT_LABELS[change.requestedShift]}
                          </span>
                        ))}
                        {calendarShiftChanges.length > 2 ? <span className="more-events">+{calendarShiftChanges.length - 2} vardiya değişikliği</span> : null}
                        {calendarMissions.slice(0, 2).map((mission) => (
                          <span className="calendar-mission-name" key={`mission-${mission.id}-${mission.userId}`}>
                            {shortLeaveName(mission.displayName)} — Görevde: {mission.location}
                          </span>
                        ))}
                        {calendarMissions.length > 2 ? <span className="more-events">+{calendarMissions.length - 2} görevli</span> : null}
                      </span>
                      <span className="calendar-cell__mobile-indicators" aria-hidden="true">
                        {approvedLeaves.length ? <i className="calendar-indicator calendar-indicator--leave">İzinli {approvedLeaves.length}</i> : null}
                        {calendarShiftChanges.length ? <i className="calendar-indicator calendar-indicator--shift">Değişim {calendarShiftChanges.length}</i> : null}
                        {calendarMissions.length ? <i className="calendar-indicator calendar-indicator--mission">Görevli {calendarMissions.length}</i> : null}
                        {offPatterns.length ? <i className="calendar-indicator calendar-indicator--off">3 OFF {offPatterns.length}</i> : null}
                      </span>
                    </> : null}
                  </button>
                  {day && offPatterns.length ? (
                    <span className="calendar-off-badges">
                      {offPatterns.slice(0, 2).map((pattern) => (
                        <button type="button" className="calendar-off-badge" key={pattern}
                          onClick={() => setSelectedOff({ date: iso, patterns: [pattern] })}
                          aria-label={`${pattern} OFF personelini göster`}>
                          {personalShiftLabel(pattern, iso, "OFF")}
                        </button>
                      ))}
                      {offPatterns.length > 2 ? (
                        <button type="button" className="calendar-off-more"
                          onClick={() => setSelectedOff({ date: iso, patterns: offPatterns.slice(2) })}>
                          +{offPatterns.length - 2} ekip
                        </button>
                      ) : null}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : view === "week" ? (
        <div className="week-view">
          {filteredDays.map((day) => (
            <article className="week-day" key={day.date}>
              <header>
                <div>
                  <span>{dateLabel(day.date, "short")}</span>
                  <strong>
                    {format(parseISO(day.date), "EEEE", { locale: tr })}
                  </strong>
                </div>
                <CalendarShiftBadge
                  shift={displayedShift(day)}
                  label={displayedShiftLabel(day)}
                />
              </header>
              {day.counts ? (
                <div className="week-counts">
                  {([
                    ["expected", "beklenen"], ["off", "OFF"], ["approvedLeave", "izinli"],
                    ["pendingLeave", "bekleyen"], ["absence", "diğer yokluk"],
                    ["shiftChanged", "değişiklik"], ["onDuty", "görevde"], ["remaining", "kalan çalışan"],
                  ] as const).filter(([key]) => day.counts?.[key]).map(([key, label]) => (
                    <span className={key === "remaining" ? "remaining" : ""} key={key}>
                      <b>{day.counts?.[key]}</b> {label}
                    </span>
                  ))}
                  {!Object.values(day.counts).some(Boolean) ? <p className="week-counts__empty">Bu gün için olay veya personel özeti yok.</p> : null}
                </div>
              ) : null}
              <button className="text-link" onClick={() => setSelected(day)}>
                Gün ayrıntısı
              </button>
            </article>
          ))}
        </div>
      ) : (
        <div className="calendar-list">
          {listEntries.length ? (
            listEntries.map((entry) => {
              const event = entry.event;
              return (
                <article
                  key={entry.key}
                >
                  <time>{dateRangeLabel(entry.startDate, entry.endDate)}</time>
                  <span className={`team-dot team-dot--${team.toLowerCase()}`}>
                    {team}
                  </span>
                  <div>
                    <strong>{event.displayName}</strong>
                    <span>
                      {"type" in event
                        ? LEAVE_LABELS[event.type]
                        : "location" in event
                          ? `Görevde: ${event.location}`
                          : `${SHIFT_LABELS[event.requestedShift]} gelecek`}
                    </span>
                  </div>
                  {"status" in event ? <StatusBadge status={event.status} /> : <span className="mission-status mission-status--active">Görevde</span>}
                  <button
                    className="text-link"
                    onClick={() => openListEntry(entry)}
                  >
                    Ayrıntı
                  </button>
                </article>
              );
            })
          ) : (
            <Empty
              icon={<Search />}
              title="Kayıt bulunamadı"
              text="Seçili filtrelerde takvim kaydı yok."
            />
          )}
        </div>
      )}
      <Modal open={monthPickerOpen} onClose={() => setMonthPickerOpen(false)} title="Ay ve yıl seç">
        <div className="month-picker">
          <div className="month-picker__year">
            <button type="button" className="icon-button" aria-label="Önceki yıl" onClick={() => setMonthPickerYear((year) => year - 1)}><ChevronLeft /></button>
            <strong>{monthPickerYear}</strong>
            <button type="button" className="icon-button" aria-label="Sonraki yıl" onClick={() => setMonthPickerYear((year) => year + 1)}><ChevronRight /></button>
          </div>
          <div className="month-picker__grid">
            {Array.from({ length: 12 }, (_, month) => {
              const active = cursor.getFullYear() === monthPickerYear && cursor.getMonth() === month;
              return <button
                type="button"
                className={active ? "active" : ""}
                aria-pressed={active}
                onClick={() => { setCursor(new Date(monthPickerYear, month, 1)); setMonthPickerOpen(false); }}
                key={month}
              >
                {format(new Date(monthPickerYear, month, 1), "LLLL", { locale: tr })}
              </button>;
            })}
          </div>
        </div>
      </Modal>
      <Modal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected ? dateLabel(selected.date) : "Gün ayrıntısı"}
        wide
      >
        {selected && unit === "R1_R2" ? <ManagementDayDetails date={selected.date} skill={skill} /> : selected ? (
          <DayDetails
            day={selected}
            displayShift={selected.teamShift}
            displayShiftLabel={SHIFT_LABELS[selected.teamShift]}
            skill={skill}
            handovers={data?.handovers?.filter(
              (handover) => handover.date === selected.date,
            )}
          />
        ) : null}
      </Modal>
      <Modal
        open={Boolean(selectedOff)}
        onClose={() => setSelectedOff(null)}
        title={selectedOff ? `${dateLabel(selectedOff.date)} · ${selectedOff.patterns.join(", ")} OFF` : "Alt ekip OFF"}
      >
        {selectedOff ? (() => {
          const day = byDate.get(selectedOff.date);
          const people = (day?.people ?? []).filter((item) =>
            selectedOff.patterns.includes(item.schedulePattern as SchedulePattern),
          );
          return people.length ? (
            <div className="people-shifts off-roster">
              {people.map((item) => {
                const profile = personnelById.get(String(item.userId));
                return (
                  <article key={item.userId}>
                    <div>
                      <strong>{item.displayName}</strong>
                      <span>{profile?.title ?? "Ünvan belirtilmemiş"} · {item.schedulePattern}</span>
                      {profile?.skills?.length ? (
                        <span className="profile-badges">
                          {profile.skills.map((skill) => <b key={skill}>{skillLabel(skill)}</b>)}
                        </span>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <Empty icon={<Users />} title="Personel bulunamadı" text="Bu alt düzende aktif personel bulunmuyor." />
          );
        })() : null}
      </Modal>
    </div>
  );
}
